///
/// EarthBrowser
///
/// Created by Matt Giger on 9/2/2026
/// Copyright (c) 2026 Geodata Labs
///

import { PrecipFlowTextureSeries } from "@/earthbrowser/dataset-distribution/imerg/PrecipFlowTextureSeries"
import { TextureOverlay } from "@/earthbrowser/datasets/TextureOverlay"
import { precipAltitudeScale } from "@/earthbrowser/datasets/precipAltitude"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { wgs } from "@/earthbrowser/utils/geographic"
import { type Material } from "@stovokor/shader/Material"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"

/**
 * Global precipitation overlay from NASA GPM IMERG Early Run (satellite
 * estimate, ~10 km / 30-minute frames). Complements the CONUS-only MRMS radar
 * overlay: the shader suppresses IMERG inside the MRMS bounds so the sharper
 * ground radar wins where both are available.
 *
 * Frames are advected along estimated optical flow while blending (see
 * PrecipFlowTextureSeries), so precipitation translates with moving systems
 * over the 30-minute frame interval instead of cross-dissolving.
 */
export class ImergOverlay extends TextureOverlay {
	private flowSeries: PrecipFlowTextureSeries

	constructor(context: EarthBrowserContext) {
		super(context)
		this.flowSeries = new PrecipFlowTextureSeries(context)
		// One RG texture for the frame pair: see TextureSeries.packPairs
		this.flowSeries.packPairs = true
		this.textureSeries = this.flowSeries
	}

	public overlayMaterial(): Material {
		return new ImergOverlayMaterial(this.flowSeries)
	}
}

class ImergOverlayMaterial implements Material {
	public shaderDefines: Record<string, string> = {
		"IMERG_TEXTURES": "1"
	}
	public available: boolean = true
	public transparency: boolean = false
	public imergFade = new UniformDynamic<number>()
	public imergBlend = new UniformDynamic<number>()
	public imergPair = new UniformDynamic<Texture>()
	public imergFlow = new UniformDynamic<Texture>()
	// Echo opacity ramp by camera altitude (datasets/precipAltitude.ts)
	public imergAltitudeScale = new UniformDynamic<number>()

	private textureSeries: PrecipFlowTextureSeries

	constructor(textureSeries: PrecipFlowTextureSeries) {
		this.textureSeries = textureSeries
	}

	public renderUpdate(state: RenderState): void {
		this.imergFade.value = this.textureSeries.fadeFactor
		this.imergBlend.value = this.textureSeries.blendFactor
		this.imergPair.value = this.textureSeries.pairTexture
		this.imergFlow.value = this.textureSeries.flowTexture
		this.imergAltitudeScale.value = precipAltitudeScale(state.camera.position.length - wgs.a)
	}
}
